"use client";

import type { Config } from "@puckeditor/core";
import { emptyLexicalContent } from "./designHelpers";
import { LayoutDivider, LayoutSpacer } from "./components/LayoutDivider";
import { PageDisclosure } from "./components/PageDisclosure";
import { PageHeader } from "./components/PageHeader";
import { PageLinks } from "./components/PageLinks";
import { PageLegalLinks } from "./components/PageLegalLinks";
import { RichTextField } from "./components/RichTextField";
import { OptionalNumberField } from "./components/OptionalNumberField";
import type { PageChromeBlockProps, PageChromeContext } from "./pageChromeTypes";

/** Leaf blocks compose with Sections, Columns and Cards without imposing a page layout. */
export function createPageChromeBlocks(
  context: PageChromeContext = {}
): Config<PageChromeBlockProps>["components"] {
  return {
    PageHeader: {
      label: "Page header",
      fields: {
        variant: {
          type: "radio",
          label: "Header style",
          options: [
            { label: "Centered logo", value: "centered" },
            { label: "Logo and action", value: "campaign" },
          ],
        },
        ctaLabel: { type: "text", label: "Action label" },
        sticky: {
          type: "radio",
          label: "Scroll behavior",
          options: [
            { label: "Scroll with page", value: false },
            { label: "Stay at top", value: true },
          ],
        },
        showProgress: {
          type: "radio",
          label: "Form progress",
          options: [
            { label: "Show", value: true },
            { label: "Hide", value: false },
          ],
        },
      },
      defaultProps: {
        variant: "centered",
        ctaLabel: "Free Matching",
        sticky: true,
        showProgress: true,
      },
      render: (props) => <PageHeader {...props} {...context} />,
    },
    Links: {
      label: "Links",
      fields: {
        label: { type: "text", label: "Navigation label" },
        appearance: {
          type: "radio",
          label: "Link style",
          options: [
            { label: "Legal links", value: "legal" },
            { label: "Navigation", value: "navigation" },
          ],
        },
        align: {
          type: "radio",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
          ],
        },
        links: {
          type: "array",
          label: "Links",
          max: 12,
          getItemSummary: (item) => item.label || "Link",
          defaultItemProps: { label: "New link", url: "/", newTab: false },
          arrayFields: {
            label: { type: "text", label: "Label" },
            url: { type: "text", label: "URL" },
            newTab: {
              type: "radio",
              label: "Open in",
              options: [
                { label: "Same tab", value: false },
                { label: "New tab", value: true },
              ],
            },
          },
        },
      },
      defaultProps: {
        label: "Legal links",
        appearance: "legal",
        align: "center",
        links: [
          { label: "Privacy Policy", url: "/privacy-policy", newTab: true },
          { label: "Terms and Conditions", url: "/terms-and-conditions", newTab: true },
        ],
      },
      render: (props) => <PageLinks {...props} inert={context.inert} />,
    },
    LegalLinks: {
      label: "Site-wide legal links",
      fields: {},
      defaultProps: {},
      render: () => (
        <PageLegalLinks
          config={context.legalLinks}
          inert={context.inert}
          readOnly={context.readOnly}
        />
      ),
    },
    Disclosure: {
      label: "Disclosure",
      fields: {
        label: { type: "text", label: "Disclosure label" },
        appearance: {
          type: "radio",
          label: "Appearance",
          options: [
            { label: "Standard", value: "standard" },
            { label: "Supporting content", value: "supporting" },
          ],
        },
        content: {
          type: "custom",
          label: "Disclosure text",
          render: ({ id, value, onChange, readOnly }) => (
            <RichTextField
              id={id}
              value={value}
              onChange={onChange}
              readOnly={readOnly || context.readOnly}
              media={context.media}
            />
          ),
        },
        openByDefault: {
          type: "radio",
          label: "Initial state",
          options: [
            { label: "Collapsed", value: false },
            { label: "Expanded", value: true },
          ],
        },
      },
      defaultProps: {
        label: "Sponsored Schools Disclosure",
        content: emptyLexicalContent("Add your disclosure text."),
        openByDefault: false,
      },
      render: (props) => <PageDisclosure {...props} media={context.media} inert={context.inert} />,
    },
    Divider: {
      label: "Divider",
      fields: {
        width: {
          type: "radio",
          label: "Line width",
          options: [
            { label: "Fill container", value: "full" },
            { label: "Narrow", value: "narrow" },
          ],
        },
      },
      defaultProps: { width: "full" },
      render: (props) => <LayoutDivider {...props} />,
    },
    Spacer: {
      label: "Spacer",
      fields: {
        spacingUnit: {
          type: "radio",
          label: "Spacing scale",
          options: [
            { label: "Fixed", value: "px" },
            { label: "Follow site text scale", value: "rem" },
          ],
        },
        heightPx: {
          type: "custom",
          label: "Custom height (px)",
          render: ({ id, value, onChange, readOnly }) => (
            <OptionalNumberField
              id={id}
              value={value}
              onChange={onChange}
              readOnly={readOnly || context.readOnly}
              label="Custom height (px)"
              min={0}
              max={160}
            />
          ),
        },
        size: {
          type: "radio",
          label: "Height",
          options: [
            { label: "Small", value: "small" },
            { label: "Medium", value: "medium" },
            { label: "Large", value: "large" },
          ],
        },
      },
      defaultProps: { size: "medium" },
      render: (props) => <LayoutSpacer {...props} />,
    },
  };
}
